import React, { useEffect, useState } from "react";
import { X, CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";

// Standard ADL categories. The approved_activities list on the back
// end is free-form strings, but offering a checklist speeds up the
// common case where ForwardHealth authorizes the same ADL buckets the
// RN captured on Step 2.
const STANDARD_ACTIVITIES = [
  "Bathing",
  "Dressing",
  "Eating / Meal prep",
  "Mobility",
  "Toileting",
  "Transferring",
  "Medication reminder",
  "Light housekeeping",
];

/**
 * PaOutcomeModal — Admin modal that captures a ForwardHealth PA outcome
 * on a completed draft and regenerates the Client Care Plan as the Final
 * (approved) or Denied variant.
 *
 * Props:
 *   open        — boolean
 *   draft       — row object from /intake/drafts/pending (expects
 *                 draft_id, client_name, job_id, pa_outcome, pipeline_id)
 *   onClose     — () => void
 *   onConfirm   — async ({outcome, ...fields}) => void. Throws on error
 *                 so the modal can render the message inline.
 */
export default function PaOutcomeModal({ open, draft, onClose, onConfirm }) {
  const [outcome, setOutcome] = useState("approved");
  const [paNumber, setPaNumber] = useState("");
  const [approvalDate, setApprovalDate] = useState("");
  const [approvedUnits, setApprovedUnits] = useState("");
  const [certFrom, setCertFrom] = useState("");
  const [certTo, setCertTo] = useState("");
  const [selectedActivities, setSelectedActivities] = useState(() => new Set());
  const [activityNotes, setActivityNotes] = useState("");
  const [denialDate, setDenialDate] = useState("");
  const [denialReason, setDenialReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setOutcome("approved");
      setPaNumber("");
      setApprovalDate("");
      setApprovedUnits("");
      setCertFrom("");
      setCertTo("");
      setSelectedActivities(new Set());
      setActivityNotes("");
      setDenialDate("");
      setDenialReason("");
      setBusy(false);
      setError("");
    }
  }, [open, draft?.draft_id]);

  if (!open || !draft) return null;

  const toggleActivity = (a) => {
    setSelectedActivities((prev) => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a); else next.add(a);
      return next;
    });
  };

  const validApproval = outcome !== "approved" || (approvalDate && paNumber);
  const validDenial = outcome !== "denied" || (denialDate && denialReason);
  const canSubmit = !busy && validApproval && validDenial;

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const extras = [];
      if (activityNotes.trim()) {
        extras.push(...activityNotes.split(",").map((s) => s.trim()).filter(Boolean));
      }
      const approved_activities = [...selectedActivities, ...extras];
      await onConfirm({
        outcome,
        approved_units_per_week: approvedUnits.trim(),
        cert_period_from: certFrom.trim(),
        cert_period_to: certTo.trim(),
        approved_activities,
        approval_date: approvalDate.trim(),
        pa_number: paNumber.trim(),
        denial_date: denialDate.trim(),
        denial_reason: denialReason.trim(),
      });
    } catch (e) {
      setError(e?.message || "Failed to record PA outcome");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="pa-outcome-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-[#D6E3EF] max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#D6E3EF]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275] font-semibold">
              ForwardHealth · PA outcome
            </div>
            <h2 className="font-display text-xl font-semibold text-[#0B1E3A]">
              Record PA outcome
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-[#3A5275] hover:bg-[#F1F5F9]"
            data-testid="pa-outcome-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="rounded-lg border border-[#B7CADA] bg-[#EEF5FC] px-3 py-2 text-[12px] text-[#0B1E3A]" data-testid="pa-outcome-draft-summary">
            <div className="font-semibold">{draft.client_name || "(unnamed client)"}</div>
            <div className="font-mono text-[11px] text-[#3A5275] mt-0.5">
              draft: {draft.draft_id}
              {draft.pipeline_id ? ` · pipeline: ${draft.pipeline_id}` : ""}
            </div>
          </div>

          {/* Outcome toggle */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setOutcome("approved")}
              className={[
                "rounded-xl border-2 px-4 py-3 text-left transition-all",
                outcome === "approved"
                  ? "border-[#3a8f5a] bg-[#E6F5EC]"
                  : "border-[#D6E3EF] bg-white hover:border-[#B5D9C3]",
              ].join(" ")}
              data-testid="pa-outcome-tab-approved"
              aria-pressed={outcome === "approved"}
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className={`w-5 h-5 ${outcome === "approved" ? "text-[#1e5a36]" : "text-[#3A5275]"}`} />
                <div className="font-semibold text-[14px] text-[#0B1E3A]">PA Approved</div>
              </div>
              <div className="text-[11px] text-[#3A5275] mt-1">
                ForwardHealth authorized — will generate the Final Care Plan.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setOutcome("denied")}
              className={[
                "rounded-xl border-2 px-4 py-3 text-left transition-all",
                outcome === "denied"
                  ? "border-[#C05746] bg-[#FBEAE5]"
                  : "border-[#D6E3EF] bg-white hover:border-[#E6B8B8]",
              ].join(" ")}
              data-testid="pa-outcome-tab-denied"
              aria-pressed={outcome === "denied"}
            >
              <div className="flex items-center gap-2">
                <XCircle className={`w-5 h-5 ${outcome === "denied" ? "text-[#8A2E1A]" : "text-[#3A5275]"}`} />
                <div className="font-semibold text-[14px] text-[#0B1E3A]">PA Denied</div>
              </div>
              <div className="text-[11px] text-[#3A5275] mt-1">
                Not authorized — will generate a "Do Not Distribute" stamp.
              </div>
            </button>
          </div>

          {outcome === "approved" && (
            <div className="space-y-4" data-testid="pa-outcome-approved-fields">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-[#3A5275] mb-1">
                    Approval date <span className="text-[#A42A2A]">*</span>
                  </label>
                  <input
                    type="date"
                    value={approvalDate}
                    onChange={(e) => setApprovalDate(e.target.value)}
                    className="w-full rounded-md border border-[#D6E3EF] px-3 py-2 text-[13px]"
                    data-testid="pa-outcome-approval-date"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-[#3A5275] mb-1">
                    PA number <span className="text-[#A42A2A]">*</span>
                  </label>
                  <input
                    type="text"
                    value={paNumber}
                    onChange={(e) => setPaNumber(e.target.value)}
                    placeholder="e.g. PA1234567"
                    className="w-full rounded-md border border-[#D6E3EF] px-3 py-2 text-[13px] font-mono"
                    data-testid="pa-outcome-pa-number"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-[#3A5275] mb-1">
                    Approved units per week
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={approvedUnits}
                    onChange={(e) => setApprovedUnits(e.target.value)}
                    placeholder="e.g. 14"
                    className="w-full rounded-md border border-[#D6E3EF] px-3 py-2 text-[13px]"
                    data-testid="pa-outcome-units"
                  />
                </div>
                <div />
                <div>
                  <label className="block text-[11px] font-semibold text-[#3A5275] mb-1">
                    Cert period — from
                  </label>
                  <input
                    type="date"
                    value={certFrom}
                    onChange={(e) => setCertFrom(e.target.value)}
                    className="w-full rounded-md border border-[#D6E3EF] px-3 py-2 text-[13px]"
                    data-testid="pa-outcome-cert-from"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-[#3A5275] mb-1">
                    Cert period — to
                  </label>
                  <input
                    type="date"
                    value={certTo}
                    onChange={(e) => setCertTo(e.target.value)}
                    className="w-full rounded-md border border-[#D6E3EF] px-3 py-2 text-[13px]"
                    data-testid="pa-outcome-cert-to"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-[#3A5275] mb-1.5">
                  Approved activities
                </label>
                <div className="grid grid-cols-2 gap-1.5" data-testid="pa-outcome-activities">
                  {STANDARD_ACTIVITIES.map((a) => (
                    <label
                      key={a}
                      className="inline-flex items-center gap-2 text-[12px] text-[#0B1E3A] rounded border border-[#D6E3EF] px-2 py-1 cursor-pointer hover:bg-[#F1F5F9]"
                    >
                      <input
                        type="checkbox"
                        checked={selectedActivities.has(a)}
                        onChange={() => toggleActivity(a)}
                        data-testid={`pa-outcome-activity-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      />
                      {a}
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  value={activityNotes}
                  onChange={(e) => setActivityNotes(e.target.value)}
                  placeholder="Additional authorized activities (comma-separated)"
                  className="mt-2 w-full rounded-md border border-[#D6E3EF] px-3 py-2 text-[12px]"
                  data-testid="pa-outcome-activity-notes"
                />
              </div>
            </div>
          )}

          {outcome === "denied" && (
            <div className="space-y-4" data-testid="pa-outcome-denied-fields">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-[#3A5275] mb-1">
                    Denial date <span className="text-[#A42A2A]">*</span>
                  </label>
                  <input
                    type="date"
                    value={denialDate}
                    onChange={(e) => setDenialDate(e.target.value)}
                    className="w-full rounded-md border border-[#D6E3EF] px-3 py-2 text-[13px]"
                    data-testid="pa-outcome-denial-date"
                  />
                </div>
                <div />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#3A5275] mb-1">
                  Denial reason <span className="text-[#A42A2A]">*</span>
                </label>
                <textarea
                  value={denialReason}
                  onChange={(e) => setDenialReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Insufficient documentation of functional impairment"
                  className="w-full rounded-md border border-[#D6E3EF] px-3 py-2 text-[13px]"
                  data-testid="pa-outcome-denial-reason"
                />
              </div>
              <div className="rounded-lg border border-[#E6B8B8] bg-[#FBEAE5] px-3 py-2 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#8A2E1A] mt-0.5 shrink-0" />
                <div className="text-[12px] text-[#8A2E1A]">
                  A Denied Care Plan PDF will be stored for the agency record. It is
                  clearly stamped "Do Not Distribute" so the client &amp; PCW are never
                  handed an un-authorized plan.
                </div>
              </div>
            </div>
          )}

          {error && (
            <div
              className="rounded-lg border border-[#E6B8B8] bg-[#FBEAE5] px-3 py-2 text-[12px] text-[#8A2E1A]"
              data-testid="pa-outcome-error"
            >
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#D6E3EF] bg-[#F7FAFD] rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost"
            data-testid="pa-outcome-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={outcome === "approved" ? "btn-primary" : "btn-primary"}
            data-testid="pa-outcome-confirm"
            style={outcome === "denied"
              ? { background: "linear-gradient(180deg,#A42A2A,#7A1F1F)" }
              : undefined}
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
            ) : outcome === "approved" ? (
              <><CheckCircle2 className="w-4 h-4" /> Record approval &amp; generate Final Care Plan</>
            ) : (
              <><XCircle className="w-4 h-4" /> Record denial</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
